import React from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ShieldCheck } from "lucide-react";

/** Final signup step — name, optional email/password and business fields. */
/** "You'll get a ₹Y joining bonus…" text for a validated code in new_member/both mode. */
export const joiningBonusHint = (refCheck) => {
  if (!refCheck?.valid || !(Number(refCheck.new_member_credit_amount) > 0)) return "";
  const plans = (refCheck.qualifying_plan_names || []).join(" or ") || "a qualifying plan";
  return `You'll get a ₹${Number(refCheck.new_member_credit_amount).toLocaleString("en-IN")} joining bonus in your Pool Balance when you buy ${plans}.`;
};

const ReferralField = ({ form, setForm, refCheck, onBlur, onChange }) => (
  <div>
    <Label htmlFor="signup-referral-code">Referral code <span className="text-muted-foreground text-xs">(optional)</span></Label>
    <Input id="signup-referral-code" data-testid="signup-referral-code" className="font-mono uppercase tracking-wider"
           value={form.referral_code} maxLength={12} placeholder="e.g. 7KQ2MXP" autoComplete="off"
           onChange={(e) => { setForm((f) => ({ ...f, referral_code: e.target.value.toUpperCase().replace(/\s/g, "") })); onChange(); }}
           onBlur={onBlur} aria-invalid={refCheck ? !refCheck.valid : undefined} />
    {refCheck && (
      <p className={`text-xs mt-1 ${refCheck.valid ? "text-primary" : "text-destructive"}`} data-testid="signup-referral-status">
        {refCheck.valid ? `Referred by ${refCheck.referrer_first_name}` : refCheck.message || "Invalid referral code"}
      </p>
    )}
    {joiningBonusHint(refCheck) && (
      <p className="text-xs mt-1 rounded-md bg-secondary/10 text-secondary px-2 py-1.5" data-testid="signup-joining-bonus">{joiningBonusHint(refCheck)}</p>
    )}
  </div>
);

export const SignupDetailsForm = ({ role, form, update, setForm, categories, busy, onSubmit, refCheck, onReferralBlur, onReferralChange }) => (
  <form className="mt-5 space-y-4" onSubmit={onSubmit}>
    <div className="rounded-lg bg-primary/10 text-primary border border-primary/20 px-3 py-2 text-xs flex items-center gap-2">
      <ShieldCheck className="w-3.5 h-3.5" /> Phone verified · {form.phone}
    </div>
    <div>
      <Label>{role === "business" ? "Business name" : "Full name"}</Label>
      <Input data-testid="signup-name" value={form.name} onChange={update("name")} required
             placeholder={role === "business" ? "The Cozy Cafe" : "Your full name"} />
    </div>
    <div>
      <Label>Email <span className="text-muted-foreground text-xs">(optional)</span></Label>
      <Input data-testid="signup-email" type="email" value={form.email} onChange={update("email")}
             placeholder="you@example.com" />
    </div>
    <div>
      <Label>Password <span className="text-muted-foreground text-xs">(optional — you can always log in with OTP)</span></Label>
      <Input data-testid="signup-password" type="password" value={form.password}
             onChange={update("password")} placeholder="at least 6 characters" minLength={6} />
    </div>
    {role === "guest" && (
      <ReferralField form={form} setForm={setForm} refCheck={refCheck}
                     onBlur={onReferralBlur} onChange={onReferralChange} />
    )}
    {role === "business" && (
      <>
        <div>
          <Label>Category</Label>
          <Select value={form.business_category}
                  onValueChange={(v) => setForm((f) => ({ ...f, business_category: v }))}>
            <SelectTrigger data-testid="signup-category"><SelectValue placeholder="Choose category" /></SelectTrigger>
            <SelectContent>
              {categories.map((c) => <SelectItem key={c.id} value={c.name}>{c.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label>Location</Label>
          <Input data-testid="signup-location" value={form.business_location}
                 onChange={update("business_location")} placeholder="City / Area" />
        </div>
      </>
    )}
    <Button data-testid="signup-submit" disabled={busy} type="submit" className="w-full bg-primary text-primary-foreground">
      {busy ? "Creating…" : `Create ${role} account`}
    </Button>
  </form>
);

export default SignupDetailsForm;
